// frontend/src/views/admin/UserManagement.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import DeleteConfirmationModal from "../../components/ui/DeleteConfirmationModal";
import { User } from "../../services/api";
import {
  UserPlus,
  Ban,
  Download,
  Trash2,
  X,
  UserCheck,
  CheckCircle,
  XCircle,
} from "lucide-react";
import {
  getUsers,
  createUser,
  toggleUserStatus,
  toggleUserDownload,
  deleteUser,
  type User as ApiUser,
  type CreateUserData,
} from "../../services/users";
import CredentialsModal from "../../components/CredentialsModal";

interface CreateUserFormProps {
  onUserCreated: () => void;
}

interface GeneratedCredentials {
  username: string;
  password: string;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onUserCreated }) => {
  const initialFormData: CreateUserData = {
    nome: "",
    cognome: "",
    email: "",
    specializzazione: "",
    struttura: "",
  };

  const [formData, setFormData] = useState<CreateUserData>(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState<GeneratedCredentials | null>(
    null
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await createUser(formData);
      setCredentials(response.data.credentials);
      setShowCredentials(true);
      setFormData(initialFormData);
      onUserCreated();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Error creating user");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="p-6 bg-white rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-4">New User</h2>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, nome: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formData.cognome}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, cognome: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialization
              </label>
              <input
                type="text"
                value={formData.specializzazione}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    specializzazione: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facility
              </label>
              <input
                type="text"
                value={formData.struttura}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    struttura: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <UserPlus size={16} />
              {isLoading ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>

      {credentials && (
        <CredentialsModal
          isOpen={showCredentials}
          onClose={() => setShowCredentials(false)}
          credentials={credentials}
        />
      )}
    </>
  );
};

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userToDelete, setUserToDelete] = useState<ApiUser | null>(null);
  const { user: currentUser, updateUserPermissions, logout } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await getUsers();
      setUsers(
        response.data.map((user) => ({
          ...user,
          canDownload: user.canDownload,
          is_active: user.is_active,
        }))
      );
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Error loading users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleStatus = async (userId: number, currentStatus: boolean) => {
    try {
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, is_active: !currentStatus } : user
        )
      );
      await toggleUserStatus(userId);
    } catch (err: any) {
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, is_active: currentStatus } : user
        )
      );
    }
  };

  const handleToggleDownload = async (
    userId: number,
    currentState: boolean
  ) => {
    try {
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                canDownload: !currentState,
              }
            : user
        )
      );
      await toggleUserDownload(userId);
      if (currentUser && currentUser.id === userId) {
        const updatedUser = await updateUserPermissions();
        if (
          updatedUser &&
          "is_active" in updatedUser &&
          !updatedUser.is_active
        ) {
          await logout();
          navigate("/login");
        }
      }
    } catch (err: any) {
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                canDownload: currentState,
              }
            : user
        )
      );
      setError(
        err.response?.data?.error?.message ||
          "Error updating download permissions"
      );
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      setUsers((prev) => prev.filter((user) => user.id !== userToDelete.id));
      await deleteUser(userToDelete.id);
    } catch (err: any) {
      await fetchUsers();
    } finally {
      setUserToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="mt-8 bg-white rounded-lg shadow-sm border p-8 text-center">
        Loading users...
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 bg-white rounded-lg shadow-sm border p-8">
        <div className="text-red-600">{error}</div>
        <button
          onClick={fetchUsers}
          className="mt-8 px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="mt-8 bg-white rounded-lg shadow-sm border">
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b">
              <th className="text-left p-3 w-[15%]">Name</th>
              <th className="text-left p-3 w-[15%]">Username</th>
              <th className="text-left p-3 w-[15%]">Specialization</th>
              <th className="text-left p-3 w-[15%]">Facility</th>
              <th className="text-left p-3 w-[10%]">Status</th>
              <th className="text-left p-3 w-[10%]">Download</th>
              <th className="text-left p-3 w-[10%]">Created At</th>
              <th className="text-center p-3 w-[10%]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b">
                <td className="p-3">
                  {user.nome} {user.cognome}
                </td>
                <td className="p-3">{user.username}</td>
                <td className="p-3">{user.specializzazione}</td>
                <td className="p-3">{user.struttura}</td>
                <td className="p-4">
                  {user.is_active ? (
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <CheckCircle size={16} />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-600">
                      <XCircle size={16} />
                      Inactive
                    </span>
                  )}
                </td>
                <td className="p-4">
                  {user.canDownload ? (
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <CheckCircle size={16} />
                      Enabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-600">
                      <XCircle size={16} />
                      Disabled
                    </span>
                  )}
                </td>
                <td className="p-4">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-center gap-1">
                    <div className="relative group">
                      <button
                        className={`p-1.5 rounded-lg hover:bg-gray-100 ${
                          user.is_active ? "text-red-600" : "text-green-600"
                        }`}
                        onClick={() =>
                          handleToggleStatus(user.id, user.is_active)
                        }
                      >
                        {user.is_active ? (
                          <Ban size={16} />
                        ) : (
                          <UserCheck size={16} />
                        )}
                      </button>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {user.is_active ? "Block user" : "Activate user"}
                      </div>
                    </div>

                    <div className="relative group">
                      <button
                        className={`p-1.5 rounded-lg hover:bg-gray-100 ${
                          user.canDownload ? "text-gray-600" : "text-green-600"
                        }`}
                        onClick={() =>
                          handleToggleDownload(user.id, user.canDownload)
                        }
                      >
                        {user.canDownload ? (
                          <Download size={16} className="text-gray-400" />
                        ) : (
                          <Download size={16} />
                        )}
                      </button>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {user.canDownload
                          ? "Disable downloads"
                          : "Enable downloads"}
                      </div>
                    </div>

                    <div className="relative group">
                      <button
                        className="p-1.5 text-red-600 rounded-lg hover:bg-gray-100"
                        onClick={() => setUserToDelete(user)}
                      >
                        <Trash2 size={16} />
                      </button>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Delete user
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteConfirmationModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDelete}
        itemName={`${userToDelete?.nome} ${userToDelete?.cognome}`}
        itemType="User"
      />
    </>
  );
};

const UserManagement: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleUserCreated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">User Management</h1>
      <CreateUserForm onUserCreated={handleUserCreated} />
      <UsersList key={refreshTrigger} />
    </div>
  );
};

export default UserManagement;
